/**
 * CUSTOM
 * This code has been developed for this solution.
 */
import {
  StepCreateData,
  StepQueryData,
  StepUpdateData,
} from "../../core/models/Step";
import NetworkServiceFactory from "../../core/services/network/NetworkService";
import { DEFAULT_QUERY_DATA } from "../../core/services/network/NetworkTypes";
import { useStepsContext } from "./StepsContext";

const useStepsActions = () => {
  const {
    setStepsResponseData,
    setStepLoading,
    setSelectedStep,
    setStepsError,
    stepsResponseData,
  } = useStepsContext();

  const dispatchGetSteps = async (
    query: StepQueryData = DEFAULT_QUERY_DATA()
  ) => {
    const networkService = NetworkServiceFactory.shared;
    try {
      const data = await networkService.getSteps(query);
      setStepsResponseData(data);
      setStepsError(undefined);
      return data;
    } catch (e: any) {
      setStepsError(e);
    } finally {
      setStepLoading(false);
    }
  };

  const dispatchGetStep = (StepId: string) => {
    const networkService = NetworkServiceFactory.shared;
    networkService
      .getStep(StepId)
      .then((step) => {
        setSelectedStep(step);
        setStepsError(undefined);
      })
      .catch((e) => {
        setStepsError(e);
      });
  };

  const dispatchCreateStep = (query: StepCreateData) => {
    const asyncCall = async () => {
      try {
        const data = await NetworkServiceFactory.shared.postStep(query);
        try {
          if (stepsResponseData) {
            const data =
              await NetworkServiceFactory.shared.getSteps(stepsResponseData);
            setStepsResponseData(data);
          }
        } catch (e: any) {
          setStepsError(e);
        }
        setSelectedStep(data);
        setStepsError(undefined);
      } catch (e: any) {
        setStepsError(e);
      }
    };
    asyncCall().catch();
  };
  const dispatchUpdateStepsBatch = async (steps: StepUpdateData[]) => {
    try {
      const updatedSteps = steps.map((step, index) => ({
        ...step,
        order: index + 1, // Ensure correct ordering
      }));
      await Promise.all(
        updatedSteps.map(async (step) => {
          try {
            await NetworkServiceFactory.shared.patchStep(step);
          } catch (error: any) {
            setStepsError(error);
          }
        })
      );

      // Fetch updated steps only if `stepsResponseData` is available
      if (stepsResponseData) {
        try {
          const refreshedSteps =
            await NetworkServiceFactory.shared.getSteps(stepsResponseData);
          setStepsResponseData(refreshedSteps);
        } catch (error) {
          console.warn("Failed to refresh steps:", error);
        }
      }
    } catch (error) {
      console.error("Error in dispatchUpdateStepsBatch:", error);
    }
  };

  const dispatchUpdateSingleStep = (query: StepUpdateData) => {
    const asyncCall = async () => {
      try {
        const data = await NetworkServiceFactory.shared.patchStep(query);

        try {
          if (stepsResponseData) {
            const refreshedSteps =
              await NetworkServiceFactory.shared.getSteps(stepsResponseData);
            setStepsResponseData(refreshedSteps);
          }
        } catch (e) {
          console.warn("Failed to refresh steps:", e);
        }

        setSelectedStep(data);
        setStepsError(undefined);
      } catch (e: any) {
        setStepsError(e);
      }
    };

    asyncCall().catch();
  };

  const dispatchCleanStepsContext = () => {
    setStepsResponseData(undefined);
    setStepsError();
    setSelectedStep();
  };

  return {
    dispatchGetSteps,
    dispatchGetStep,
    dispatchCleanStepsContext,
    dispatchCreateStep,
    dispatchUpdateStepsBatch,
    dispatchUpdateSingleStep,
  };
};

export { useStepsActions };
